import { useRef, useMemo } from 'react';
import { useInfiniteQuery, InfiniteData } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { QUERY_KEYS } from '@restworld/utils-common';
import { useOrganizationsService, IndexCompanies } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';
import { Company } from '@restworld/utility-types';

const DEFAULT_OFFSET = 0;
interface EmployerCompanyListQueryParams {
  context: string;
  organizationId?: string;
  limit?: number;
}

function useEmployerCompanyListQuery({
  params,
  getAllCompanies,
  enabled,
}: {
  params: EmployerCompanyListQueryParams;
  getAllCompanies?: boolean;
  enabled?: boolean;
}) {
  const { limit = 1, ...restParams } = params;
  const organisationsService = useOrganizationsService();
  const { user } = useAuth();

  const queryKey = useEmployerCompanyListQueryKey(getAllCompanies);
  const nextPageRef = useRef<number>(undefined);
  const hasNextPageRef = useRef<boolean>(undefined);

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => {
      nextPageRef.current = pageParam + 1;
      return organisationsService.listCompanies({
        offset: pageParam * limit,
        organizationId: user?.organization_id,
        limit,
        filterDefault: true,
        ...restParams,
      });
    },
    getNextPageParam: (lastPage) => {
      const totalHits = lastPage.data.hits;
      const companiesLoadedSoFar = nextPageRef.current * limit;

      hasNextPageRef.current = companiesLoadedSoFar < totalHits;
      return nextPageRef.current;
    },
    initialPageParam: DEFAULT_OFFSET,
    enabled,
  });

  const companyListInfo = useMemo(() => extractCompanyListInfo(query.data), [query.data]);

  return {
    companyList: companyListInfo.items,
    hits: companyListInfo.hits,
    hasNextPage: hasNextPageRef.current,
    isLoading: query.isLoading,
    currentPage: nextPageRef.current, //nextPageRef.current is zero-indexed
    query,
  };
}

export default useEmployerCompanyListQuery;

type EmployerCompanyListInfo = {
  items: Company[];
  hits: number;
};

export function extractCompanyListInfo(
  data?: InfiniteData<AxiosResponse<IndexCompanies, unknown>, unknown>
): EmployerCompanyListInfo {
  if (!data || !data.pages) return { items: [], hits: 0 };
  const items = data?.pages.flatMap((page) => page.data.data);
  const hits = data?.pages.flatMap((page) => page.data.hits)[0];

  return { items, hits };
}

export function useEmployerCompanyListQueryKey(getAllCompanies?: boolean) {
  const { user } = useAuth();
  return [(QUERY_KEYS.ORGANISATIONS.COMPANY_LIST, user?.organization_id, getAllCompanies ? 'all' : '')];
}
